import { useMutation } from '@tanstack/react-query'

import { AnnotationTool, Feature, SubClassificationToolTypes, ToolTrainingData } from '@sparkai/shared-types'
import { ErrorResponse } from '@sparkai/utils'

import { apiClient } from '../../apiClient'

type TrainingFeedbackAnnotation = {
  trainingModuleResolutionAnnotationId: string
  resolutionId?: string
  featureId: string
  predictionId?: string
  taskId?: string
  engagementId: string
  mediaId: string
  parentId?: string | null
  data: ToolTrainingData
  feature: Feature
}

type ResolutionResponse = {
  trainingModuleResolutionId: string
  isCorrect: boolean
  suspensionEnded: boolean
  trainingReset: boolean
  feedback: {
    score: number
    media: {
      mediaId: string
      location: string
      filename: string
      mimeType: string
      metadata: {
        cameraName?: string
        timestamp?: number
      }
    }[]
    groundTruth: TrainingFeedbackAnnotation[]
    predictions: TrainingFeedbackAnnotation[]
    resolution: TrainingFeedbackAnnotation[]
    escalation?: {
      submitted: { isEscalated: boolean; escalationReason: string | null }
      groundTruth: { isEscalated: boolean; escalationReason: string | null }
    }
  }
}

type MetadataBase = {
  mediaId: string
  imageIdx?: number
}

type ShapeMetadata = MetadataBase & {
  class: string
  tool: AnnotationTool
  vertices: { x: number; y: number }[]
}

type ClassificationMetadata = MetadataBase & {
  type: AnnotationTool
  values: string[]
}

export type ResolutionRequest = {
  engagementId: string
  timeToClose: number
  response: string
  annotations: {
    featureId: string
    featurePredictionId?: string
    metadata: ShapeMetadata | ClassificationMetadata
    subClassifications?: { featureId: string; type: SubClassificationToolTypes; values: string[] }[]
  }[]
  isEscalated?: boolean
  escalationReason?: string | null
}

export function useSendResolution(sessionId?: string) {
  return useMutation<ResolutionResponse, ErrorResponse, ResolutionRequest>({
    mutationFn: async (resolution: ResolutionRequest) => {
      const { data } = await apiClient.post<ResolutionResponse>(
        `/int/v3/engagements/training/sessions/${sessionId}/resolutions`,
        { ...resolution }
      )
      return data
    },
  })
}
